/// <reference types="google.maps" />
import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/google-maps-loader";
import { isDemoModeEnabled } from "@/lib/demo-mode";
import { getPlacePhoto, searchPlace } from "@/lib/maps.functions";
import { orderLog } from "@/lib/debug-order";
import { splitRouteIndicesByDay } from "@/lib/map-day-routing";
import { Loader2, Navigation } from "lucide-react";

type Mode = "walk" | "scooter" | "bike" | "car" | "transit";

export type MapStop = { name: string; queryHint?: string; lat?: number; lng?: number; photoUrl?: string; placeId?: string };

export type MapVenue = {
  id: string;
  name: string;
  kind: "lunch" | "dinner" | "hotel";
  lat: number;
  lng: number;
  day?: number;
  googleMapsUri?: string;
};

export type ResolvedStop = {
  name: string;
  placeId?: string;
  photoUrl?: string;
  lat: number;
  lng: number;
};

export type RouteLeg = {
  from: string;
  to: string;
  minutes: number;
  distance?: string;
  steps: string[];
  /** Global stop indices; day boundaries are deliberately never connected. */
  fromIndex?: number;
  toIndex?: number;
};

const stripHtml = (value: string) => value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// Day colors for multi-day itineraries — distinct hues that work in both light and dark contexts.
const DAY_COLORS: string[] = [
  "#2563eb", // blue-600
  "#ea580c", // orange-600
  "#16a34a", // green-600
  "#9333ea", // purple-600
  "#db2777", // pink-600
  "#0891b2", // cyan-600
  "#ca8a04", // yellow-600
  "#4f46e5", // indigo-600
];

const dayColor = (day?: number) => DAY_COLORS[((day ?? 1) - 1) % DAY_COLORS.length];

const distanceMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};

const bearingDegrees = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const formatDistance = (meters: number) => meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;

const escapeHtml = (value: string) => value
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const clearDirections = (renderer?: google.maps.DirectionsRenderer | null) => {
  renderer?.set("directions", null);
};

const directionModesFor = (mode: Mode) => {
  if (mode === "walk") return [google.maps.TravelMode.WALKING];
  if (mode === "car") return [google.maps.TravelMode.DRIVING];
  if (mode === "transit") return [google.maps.TravelMode.TRANSIT, google.maps.TravelMode.WALKING];
  return [google.maps.TravelMode.BICYCLING, google.maps.TravelMode.WALKING];
};

const routeWithFallback = (
  service: google.maps.DirectionsService,
  request: Omit<google.maps.DirectionsRequest, "travelMode">,
  mode: Mode,
  done: (
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus | string,
    usedTravelMode?: google.maps.TravelMode,
  ) => void,
  departureTime?: Date | null,
) => {
  const modes = directionModesFor(mode);
  const tryMode = (index: number) => {
    const travelMode = modes[index];
    const req: google.maps.DirectionsRequest = { ...request, travelMode };
    // Live/predictive traffic for driving: Google returns duration_in_traffic
    // when drivingOptions.departureTime is set (must be now or in the future).
    if (travelMode === google.maps.TravelMode.DRIVING) {
      const dep = departureTime && departureTime.getTime() > Date.now() ? departureTime : new Date();
      req.drivingOptions = { departureTime: dep, trafficModel: google.maps.TrafficModel.BEST_GUESS };
    }
    service.route(req, (result, status) => {
      if (status === "OK" && result) {
        done(result, status, travelMode);
        return;
      }
      if (index < modes.length - 1) {
        tryMode(index + 1);
        return;
      }
      done(result, status, travelMode);
    });
  };
  tryMode(0);
};

export type PickedPoi = {
  placeId: string;
  name: string;
  lat: number;
  lng: number;
  address?: string;
  photoUrl?: string;
};

export function GoogleMapView({
  center,
  stops,
  mode,
  language,
  optimizeOrder = true,
  departureTime,
  userPosition,
  userHeading,
  navigationActive,
  activeStopIndex = 0,
  stopDays,
  venueMarkers = [],
  originOverride,
  selectMode = false,
  onPoiPicked,
  routeRevision = 0,
  onDurations,
  onResolved,
  onOrder,
  onRouteLegs,
  onRoutePolyline,
  onNavigationLeg,
}: {
  center: { lat: number; lng: number };
  stops: MapStop[];
  mode: Mode;
  language: string;
  optimizeOrder?: boolean;
  departureTime?: Date | null;
  userPosition?: { lat: number; lng: number } | null;
  userHeading?: number | null;
  navigationActive?: boolean;
  activeStopIndex?: number;
  stopDays?: number[];
  venueMarkers?: MapVenue[];
  originOverride?: { name?: string; lat: number; lng: number } | null;
  selectMode?: boolean;
  onPoiPicked?: (poi: PickedPoi) => void;
  /** Revisão canónica da rota: resultados assíncronos de revisões antigas são descartados. */
  routeRevision?: number;
  onDurations?: (perLegMinutes: number[]) => void;
  onResolved?: (resolved: ResolvedStop[]) => void;
  onOrder?: (orderedNames: string[]) => void;
  onRouteLegs?: (legs: RouteLeg[]) => void;
  onRoutePolyline?: (encodedPolyline: string | null) => void;
  onNavigationLeg?: (leg: RouteLeg | null) => void;
}) {



  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const dayRenderersRef = useRef<google.maps.DirectionsRenderer[]>([]);
  const activeRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const activePolylineRef = useRef<google.maps.Polyline | null>(null);
  const activeMarkerRef = useRef<google.maps.Marker | null>(null);
  const activeMarkerKeyRef = useRef<string | null>(null);
  const orderedResolvedRef = useRef<ResolvedStop[]>([]);
  const navRequestRef = useRef(0);
  const lastNavRouteRef = useRef<{ key: string; origin: { lat: number; lng: number }; at: number } | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const venueMarkersRef = useRef<google.maps.Marker[]>([]);
  const venueInfoRef = useRef<google.maps.InfoWindow | null>(null);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const userPulseRef = useRef<google.maps.Marker | null>(null);
  const lastUserPosRef = useRef<{ lat: number; lng: number } | null>(null);
  const smoothedHeadingRef = useRef<number | null>(null);
  const latestViewRef = useRef({ center, userPosition, navigationActive, activeStopIndex });
  const resizeRafRef = useRef<number | null>(null);
  const fittedNavigationKeyRef = useRef<string | null>(null);
  const placesRef = useRef<google.maps.places.PlacesService | null>(null);
  const programmaticChangeRef = useRef(0);
  /** Última revisão canónica aplicada: descarta callbacks de revisões antigas. */
  const routeRevisionRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapRenderRevision, setMapRenderRevision] = useState(0);
  const [mapReadyRevision, setMapReadyRevision] = useState(0);
  const [followMode, setFollowMode] = useState(true);

  // No-op wrapper kept for call-site clarity. We detect user gestures via
  // native pointer/touch events on the map container (below) instead of a
  // time-based flag — Google's zoom_changed fires asynchronously and a flag
  // window would either swallow real pinches or leak.
  const runProgrammatic = (fn: () => void) => { fn(); };

  latestViewRef.current = { center, userPosition: userPosition ?? null, navigationActive: !!navigationActive, activeStopIndex };


  // init
  useEffect(() => {
    // Modo de teste: nunca carrega o Google Maps (chamada paga).
    if (isDemoModeEnabled()) return;
    let cancelled = false;
    let cleanup: (() => void) | null = null;
    loadGoogleMaps(language)
      .then((g) => {
        if (cancelled || !ref.current) return;
        const container = ref.current;
        mapRef.current = new g.maps.Map(ref.current, {
          center,
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
          clickableIcons: false,
          styles: [
            { featureType: "poi.business", stylers: [{ visibility: "off" }] },
            { featureType: "transit.station", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
          ],
        });


        rendererRef.current = new g.maps.DirectionsRenderer({
          map: mapRef.current,
          suppressMarkers: true,
          polylineOptions: { strokeColor: "#0ea5b7", strokeWeight: 5, strokeOpacity: 0.9 },
        });
        activeRendererRef.current = new g.maps.DirectionsRenderer({
          map: mapRef.current,
          suppressMarkers: true,
          preserveViewport: true,
          polylineOptions: { strokeColor: "#2563eb", strokeWeight: 7, strokeOpacity: 0.95 },
        });
        placesRef.current = new g.maps.places.PlacesService(mapRef.current);
        // Detect real user gestures via native events on the map container —
        // these only fire for actual fingers/mice, never for our setZoom/panTo,
        // so pinch-zoom and drag always win and pause the follow camera.
        const onUserGesture = () => setFollowMode(false);
        container.addEventListener("touchstart", onUserGesture, { passive: true });
        container.addEventListener("mousedown", onUserGesture, { passive: true });
        container.addEventListener("wheel", onUserGesture, { passive: true });

        const restoreViewport = () => {
          const map = mapRef.current;
          if (!map) return;
          g.maps.event.trigger(map, "resize");
          const view = latestViewRef.current;
          if (view.navigationActive && view.userPosition) {
            const dest = orderedResolvedRef.current[view.activeStopIndex];
            if (dest) {
              const b = new g.maps.LatLngBounds();
              b.extend(view.userPosition);
              b.extend({ lat: dest.lat, lng: dest.lng });
              map.fitBounds(b, 100);
              return;
            }
            map.panTo(view.userPosition);
            return;
          }
          map.panTo(view.center);
        };
        const resizeObserver = typeof ResizeObserver !== "undefined"
          ? new ResizeObserver(() => {
              if (resizeRafRef.current != null) window.cancelAnimationFrame(resizeRafRef.current);
              resizeRafRef.current = window.requestAnimationFrame(restoreViewport);
            })
          : null;
        resizeObserver?.observe(container);
        cleanup = () => {
          container.removeEventListener("touchstart", onUserGesture);
          container.removeEventListener("mousedown", onUserGesture);
          container.removeEventListener("wheel", onUserGesture);
          resizeObserver?.disconnect();
          if (resizeRafRef.current != null) window.cancelAnimationFrame(resizeRafRef.current);
        };

        setLoading(false);
        setMapReadyRevision((cur) => cur + 1);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoading(false);
        setError(e?.message || "Não foi possível carregar o mapa.");
      });
    return () => {
      cancelled = true;
      cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // recenter
  useEffect(() => {
    if (mapRef.current) runProgrammatic(() => mapRef.current!.panTo(center));
  }, [center.lat, center.lng]);

  // POI pick mode: when on, make Google's POI icons clickable and intercept
  // clicks to resolve full place details (name, location, photo) before
  // bubbling up via onPoiPicked. Empty-area clicks are ignored.
  useEffect(() => {
    const map = mapRef.current;
    const places = placesRef.current;
    if (!map) return;
    if (selectMode) {
      map.setOptions({ clickableIcons: true, draggableCursor: "crosshair" });
    } else {
      map.setOptions({ clickableIcons: false, draggableCursor: null as unknown as string });
    }
    if (!selectMode || !onPoiPicked) return;
    const listener = map.addListener("click", (e: google.maps.IconMouseEvent) => {
      const placeId = (e as any).placeId as string | undefined;
      if (!placeId) return;
      e.stop?.();
      if (!places) return;
      places.getDetails(
        { placeId, fields: ["name", "geometry.location", "formatted_address", "photos", "place_id"] },
        async (res, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !res?.geometry?.location) return;
          let photoUrl: string | undefined;
          try {
            photoUrl = res.photos?.[0]?.getUrl({ maxWidth: 800 });
          } catch {}
          if (!photoUrl) {
            try {
              const r = await getPlacePhoto({ data: { placeId } });
              photoUrl = r?.photoUrl ?? undefined;
            } catch {}
          }
          onPoiPicked({
            placeId,
            name: res.name ?? "Local",
            lat: res.geometry.location.lat(),
            lng: res.geometry.location.lng(),
            address: res.formatted_address ?? undefined,
            photoUrl,
          });
        },
      );
    });
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [selectMode, onPoiPicked, mapReadyRevision]);




  // user position arrow (heading-aware, smoothed for stability)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!userPosition) {
      userPulseRef.current?.setMap(null);
      userPulseRef.current = null;
      userMarkerRef.current?.setMap(null);
      userMarkerRef.current = null;
      lastUserPosRef.current = null;
      smoothedHeadingRef.current = null;
      return;
    }

    // Derive heading only from movement — compass is too jittery to be stable.
    let rawHeading: number | null = null;
    if (lastUserPosRef.current) {
      const moved = distanceMeters(lastUserPosRef.current, userPosition);
      if (moved > 4) rawHeading = bearingDegrees(lastUserPosRef.current, userPosition);
    }

    // Strong low-pass filter so the arrow only turns when direction really changes.
    if (rawHeading != null) {
      const prev = smoothedHeadingRef.current;
      if (prev == null) {
        smoothedHeadingRef.current = rawHeading;
      } else {
        let diff = ((rawHeading - prev + 540) % 360) - 180;
        smoothedHeadingRef.current = (prev + diff * 0.15 + 360) % 360;
      }
    }
    if (rawHeading != null || smoothedHeadingRef.current != null) {
      lastUserPosRef.current = userPosition;
    } else {
      lastUserPosRef.current = userPosition;
    }

    const heading = smoothedHeadingRef.current;

    const pulseIcon: google.maps.Symbol = {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 22,
      fillColor: "#2563eb",
      fillOpacity: 0.18,
      strokeColor: "#2563eb",
      strokeOpacity: 0.32,
      strokeWeight: 2,
    };

    // Mode-specific glyph that sits on top of the user position — like the
    // little car/bike/person Google Maps shows during turn-by-turn.
    const glyph =
      mode === "car" ? "🚗" :
      mode === "bike" ? "🚴" :
      mode === "scooter" ? "🛴" :
      mode === "transit" ? "🚌" :
      "🚶";

    const userIcon: google.maps.Symbol = {
      // Filled circle as a base; the emoji label sits centred on it.
      path: google.maps.SymbolPath.CIRCLE,
      scale: 16,
      fillColor: "#ffffff",
      fillOpacity: 1,
      strokeColor: "#2563eb",
      strokeWeight: 3,
      anchor: new google.maps.Point(0, 0),
    };

    if (!userPulseRef.current) {
      userPulseRef.current = new google.maps.Marker({
        position: userPosition,
        map,
        zIndex: 9998,
        clickable: false,
        icon: pulseIcon,
      });
    } else {
      userPulseRef.current.setPosition(userPosition);
    }
    if (!userMarkerRef.current) {
      userMarkerRef.current = new google.maps.Marker({
        position: userPosition,
        map,
        zIndex: 10000,
        clickable: false,
        icon: userIcon,
        label: { text: glyph, fontSize: "18px" },
      });
    } else {
      userMarkerRef.current.setPosition(userPosition);
      userMarkerRef.current.setIcon(userIcon);
      userMarkerRef.current.setLabel({ text: glyph, fontSize: "18px" });
    }

    // Follow camera: while navigating, keep the user centred. On the FIRST
    // lock (or when the active stop changes), frame both user + destination
    // so the blue route is fully visible; afterwards just pan to follow and
    // respect whatever zoom the user picked. Flat map (no tilt) keeps pinch
    // zoom predictable on mobile.
    if (navigationActive && followMode) {
      const dest = orderedResolvedRef.current[activeStopIndex];
      const fitKey = `${activeStopIndex}:${dest?.lat?.toFixed(4) ?? "x"}:${dest?.lng?.toFixed(4) ?? "x"}`;
      if (dest && fittedNavigationKeyRef.current !== fitKey) {
        fittedNavigationKeyRef.current = fitKey;
        const b = new google.maps.LatLngBounds();
        b.extend(userPosition);
        b.extend({ lat: dest.lat, lng: dest.lng });
        map.fitBounds(b, 100);
      } else {
        map.panTo(userPosition);
      }
    }


  }, [userPosition?.lat, userPosition?.lng, navigationActive, mode, followMode]);

  // Restaurant / hotel markers. These are separate from tourist-stop markers so
  // meal suggestions are visible on the map without disturbing the stop order.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    venueMarkersRef.current.forEach((m) => m.setMap(null));
    venueMarkersRef.current = [];

    if (!venueMarkers.length) return;
    if (!venueInfoRef.current) venueInfoRef.current = new google.maps.InfoWindow();

    const markers = venueMarkers.map((venue, idx) => {
      const isHotel = venue.kind === "hotel";
      const color = isHotel ? "#7c3aed" : "#f97316";
      const glyph = isHotel ? "🛏️" : "🍴";
      const label = isHotel ? "Hotel" : venue.kind === "lunch" ? "Almoço" : "Jantar";
      const marker = new google.maps.Marker({
        position: { lat: venue.lat, lng: venue.lng },
        map,
        title: venue.name,
        zIndex: 2500 + idx,
        icon: {
          url: `data:image/svg+xml;utf8,${encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38">
              <path d="M19 2c-7.2 0-13 5.8-13 13 0 9.3 13 21 13 21s13-11.7 13-21C32 7.8 26.2 2 19 2Z" fill="${color}" stroke="white" stroke-width="2"/>
              <circle cx="19" cy="15" r="9" fill="white" opacity="0.96"/>
              <text x="19" y="20" text-anchor="middle" font-size="14" font-family="system-ui, sans-serif">${glyph}</text>
            </svg>`
          )}`,
          scaledSize: new google.maps.Size(38, 38),
          anchor: new google.maps.Point(19, 36),
        },
      });
      marker.addListener("click", () => {
        const mapsLink = venue.googleMapsUri
          ? `<a href="${escapeHtml(venue.googleMapsUri)}" target="_blank" rel="noopener noreferrer" style="color:#0ea5b7;font-weight:600;text-decoration:none">Abrir no Maps</a>`
          : "";
        venueInfoRef.current?.setContent(
          `<div style="max-width:220px;font-family:system-ui,sans-serif">
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:${color};font-weight:800">${escapeHtml(label)}</div>
            <div style="font-size:14px;color:#0f172a;font-weight:800;margin-top:2px">${escapeHtml(venue.name)}</div>
            ${venue.day ? `<div style="font-size:12px;color:#64748b;margin-top:2px">Dia ${venue.day}</div>` : ""}
            ${mapsLink ? `<div style="font-size:12px;margin-top:8px">${mapsLink}</div>` : ""}
          </div>`,
        );
        venueInfoRef.current?.open(map, marker);
      });
      return marker;
    });

    venueMarkersRef.current = markers;

    if (!navigationActive && orderedResolvedRef.current.length) {
      const bounds = new google.maps.LatLngBounds();
      orderedResolvedRef.current.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
      venueMarkers.forEach((v) => bounds.extend({ lat: v.lat, lng: v.lng }));
      runProgrammatic(() => map.fitBounds(bounds, 60));
    }

    return () => {
      markers.forEach((m) => m.setMap(null));
    };
  }, [mapReadyRevision, navigationActive, venueMarkers.map((v) => `${v.id}:${v.lat.toFixed(5)}:${v.lng.toFixed(5)}:${v.name}`).join("|")]);

  // When navigation starts (or active stop changes) and we DON'T have a live
  // user position yet, fit both origin and destination once so the user sees
  // the whole leg. As soon as `userPosition` is available, the follow-camera
  // effect above takes over and we skip this fit.
  useEffect(() => {
    const map = mapRef.current;
    if (!navigationActive) {
      fittedNavigationKeyRef.current = null;
      // Reset tilt/heading when leaving navigation so the overview map is flat.
      if (map) runProgrammatic(() => { map.setTilt(0); map.setHeading(0); });
    }
    if (!map || !navigationActive) return;
    if (userPosition) return; // follow-camera handles framing
    const destination = orderedResolvedRef.current[activeStopIndex];
    const origin = activeStopIndex === 0 ? center : orderedResolvedRef.current[activeStopIndex - 1];
    if (!destination && !origin) return;
    const originKey = `${origin?.lat?.toFixed(5) ?? "none"}:${origin?.lng?.toFixed(5) ?? "none"}`;
    const fitKey = `${activeStopIndex}:${originKey}:${destination?.lat.toFixed(5) ?? "none"}:${destination?.lng.toFixed(5) ?? "none"}`;
    if (fittedNavigationKeyRef.current === fitKey) return;
    fittedNavigationKeyRef.current = fitKey;
    runProgrammatic(() => {
      if (origin && destination) {
        const b = new google.maps.LatLngBounds();
        b.extend(origin as google.maps.LatLngLiteral);
        b.extend({ lat: destination.lat, lng: destination.lng });
        map.fitBounds(b, 100);
      } else {
        const focus = origin ?? destination!;
        map.panTo(focus as google.maps.LatLngLiteral);
        map.setZoom(16);
      }
    });
  }, [navigationActive, activeStopIndex, center.lat, center.lng, mapRenderRevision, userPosition]);

  // Re-enable follow only when navigation toggles on or the active stop
  // changes — NOT on every GPS tick, so a manual zoom-out sticks.
  useEffect(() => {
    if (navigationActive) setFollowMode(true);
  }, [navigationActive, activeStopIndex]);






  // active navigation: draw a loud, focused route from the user's position to the current stop
  useEffect(() => {
    const map = mapRef.current;
    const activeRenderer = activeRendererRef.current;
    const destination = orderedResolvedRef.current[activeStopIndex];
    if (!map || !activeRenderer || !navigationActive || !destination) {
      clearDirections(activeRenderer);
      activePolylineRef.current?.setMap(null);
      activeMarkerRef.current?.setMap(null);
      activePolylineRef.current = null;
      activeMarkerRef.current = null;
      activeMarkerKeyRef.current = null;
      lastNavRouteRef.current = null;
      onNavigationLeg?.(null);
      return;
    }

    const markerKey = `${activeStopIndex}:${destination.lat.toFixed(6)}:${destination.lng.toFixed(6)}`;
    if (!activeMarkerRef.current || activeMarkerKeyRef.current !== markerKey) {
      activeMarkerRef.current?.setMap(null);
      activeMarkerRef.current = new google.maps.Marker({
        position: { lat: destination.lat, lng: destination.lng },
        map,
        zIndex: 9998,
        label: { text: String(activeStopIndex + 1), color: "white", fontWeight: "800" },
        title: destination.name,
      });
      activeMarkerKeyRef.current = markerKey;
    }

    const origin = userPosition ?? (activeStopIndex === 0 ? center : orderedResolvedRef.current[activeStopIndex - 1]) ?? destination;
    const directDistance = distanceMeters(origin, destination);
    const routeKey = `${activeStopIndex}:${mode}:${destination.lat.toFixed(6)}:${destination.lng.toFixed(6)}`;
    const lastRoute = lastNavRouteRef.current;
    if (
      userPosition &&
      lastRoute?.key === routeKey &&
      Date.now() - lastRoute.at < 12000 &&
      distanceMeters(lastRoute.origin, origin) < 25
    ) {
      return;
    }
    lastNavRouteRef.current = { key: routeKey, origin: { lat: origin.lat, lng: origin.lng }, at: Date.now() };
    const requestId = ++navRequestRef.current;

    if (directDistance < 8) {
      clearDirections(activeRenderer);
      activePolylineRef.current?.setMap(null);
      activePolylineRef.current = null;
      onNavigationLeg?.({ from: "", to: destination.name, minutes: 0, distance: "0 m", steps: [] });
      return;
    }

    const ds = new google.maps.DirectionsService();
    routeWithFallback(
      ds,
      {
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
      },
      mode,
      (result, status) => {
        if (requestId !== navRequestRef.current) return;
        if (status === "OK" && result) {
          activePolylineRef.current?.setMap(null);
          activePolylineRef.current = null;
          activeRenderer.setDirections(result);
          const leg = result.routes[0]?.legs?.[0];
          const allSteps = leg?.steps ?? [];
          // Pick current step: the one whose end_location is closest ahead of the user
          let currentIdx = 0;
          let nextManeuverDist: number | null = null;
          if (userPosition && allSteps.length) {
            let best = Infinity;
            allSteps.forEach((step, i) => {
              const end = step.end_location;
              if (!end) return;
              const d = distanceMeters(userPosition, { lat: end.lat(), lng: end.lng() });
              if (d < best) { best = d; currentIdx = i; }
            });
            // If user is already past this step's end (very close), advance to next
            if (best < 15 && currentIdx < allSteps.length - 1) currentIdx += 1;
            const target = allSteps[currentIdx]?.end_location;
            if (target) nextManeuverDist = distanceMeters(userPosition, { lat: target.lat(), lng: target.lng() });
          }
          const orderedSteps = allSteps.slice(currentIdx).map((s) => stripHtml(s.instructions)).filter(Boolean);
          onNavigationLeg?.({
            from: activeStopIndex === 0 ? "" : (orderedResolvedRef.current[activeStopIndex - 1]?.name ?? ""),
            to: destination.name,
            minutes: Math.round(((leg?.duration_in_traffic?.value ?? leg?.duration?.value) ?? 0) / 60),
            distance: nextManeuverDist != null ? formatDistance(nextManeuverDist) : leg?.distance?.text,
            steps: orderedSteps,
          });
          return;
        }

        clearDirections(activeRenderer);
        activePolylineRef.current?.setMap(null);
        activePolylineRef.current = new google.maps.Polyline({
          path: [origin, { lat: destination.lat, lng: destination.lng }],
          map,
          strokeColor: "#2563eb",
          strokeOpacity: 0.95,
          strokeWeight: 7,
        });
        onNavigationLeg?.({
          from: activeStopIndex === 0 ? "" : (orderedResolvedRef.current[activeStopIndex - 1]?.name ?? ""),
          to: destination.name,
          minutes: Math.max(1, Math.round(directDistance / (mode === "walk" ? 80 : mode === "car" ? 500 : mode === "transit" ? 250 : 220))),
          distance: formatDistance(directDistance),
          steps: [],
        });
      },
      departureTime,
    );
  }, [navigationActive, activeStopIndex, userPosition?.lat, userPosition?.lng, mode, center.lat, center.lng, mapRenderRevision, onNavigationLeg]);

  // resolve stop coordinates via Places text search around center, then route
  useEffect(() => {
    const map = mapRef.current;
    const places = placesRef.current;
    const renderer = rendererRef.current;
    if (!map || !places || !renderer) return;
    if (stops.length === 0) {
      clearDirections(renderer);
      dayRenderersRef.current.forEach(clearDirections);
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      orderedResolvedRef.current = [];
      onRouteLegs?.([]);
      onRoutePolyline?.(null);
      return;
    }

    let cancelled = false;
    // Guarda de revisão: um resultado desta execução só pode aplicar-se
    // enquanto a revisão canónica da rota for a mesma.
    const effectRevision = routeRevision;
    routeRevisionRef.current = effectRevision;
    orderLog("GoogleMapView", "routing effect run", {
      optimizeOrder,
      stopsCount: stops.length,
      stops: stops.map((s) => s.name),
      mode,
      routeRevision: effectRevision,
    });
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const findInfo = async (s: MapStop): Promise<ResolvedStop> => {
      // If we already have everything (coords + photo), no lookup needed.
      if (s.lat != null && s.lng != null && s.photoUrl) {
        return { name: s.name, lat: s.lat, lng: s.lng, placeId: s.placeId, photoUrl: s.photoUrl };
      }
      // Coordenadas já conhecidas: o mapa NUNCA volta a pesquisar o local.
      // No máximo pede a foto (uma chamada, só quando há placeId).
      if (s.lat != null && s.lng != null) {
        if (s.placeId && !s.photoUrl) {
          try {
            const photo = await getPlacePhoto({ data: { placeId: s.placeId } });
            if (photo.photoUrl) {
              return { name: s.name, lat: s.lat, lng: s.lng, placeId: s.placeId, photoUrl: photo.photoUrl };
            }
          } catch {
            // Sem foto disponível: o marcador usa o fallback visual local.
          }
        }
        return { name: s.name, lat: s.lat, lng: s.lng, placeId: s.placeId, photoUrl: s.photoUrl };
      }
      try {
        const res = await searchPlace({
          data: {
            query: s.queryHint ? `${s.name} ${s.queryHint}` : s.name,
            lat: s.lat ?? center.lat,
            lng: s.lng ?? center.lng,
          },
        });
        return {
          name: s.name,
          placeId: res.placeId ?? s.placeId,
          photoUrl: res.photoUrl ?? s.photoUrl,
          lat: s.lat ?? res.lat ?? center.lat,
          lng: s.lng ?? res.lng ?? center.lng,
        };
      } catch {
        return {
          name: s.name,
          lat: s.lat ?? center.lat,
          lng: s.lng ?? center.lng,
          placeId: s.placeId,
          photoUrl: s.photoUrl,
        };
      }
    };

    Promise.all(stops.map(findInfo)).then((resolved) => {
      if (cancelled) return;
      onResolved?.(resolved);

      const renderRoute = (orderedResolved: ResolvedStop[]) => {
        // Resposta assíncrona de uma revisão antiga nunca substitui a atual.
        if (cancelled || routeRevisionRef.current !== effectRevision) return;
        orderedResolvedRef.current = orderedResolved;
        setMapRenderRevision((cur) => cur + 1);
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];
        const rawCoords = orderedResolved.map((r) => ({ lat: r.lat, lng: r.lng }));
        // Spread overlapping markers in a small circle so numbers stay readable.
        const PROX = 0.00015; // ~15 m
        const groups: number[][] = [];
        rawCoords.forEach((c, i) => {
          const g = groups.find((idxs) => {
            const ref = rawCoords[idxs[0]];
            return Math.abs(ref.lat - c.lat) < PROX && Math.abs(ref.lng - c.lng) < PROX;
          });
          if (g) g.push(i);
          else groups.push([i]);
        });
        const displayCoords = rawCoords.slice();
        groups.forEach((idxs) => {
          if (idxs.length < 2) return;
          const radius = 0.00022; // ~22 m
          idxs.forEach((idx, k) => {
            const angle = (2 * Math.PI * k) / idxs.length;
            displayCoords[idx] = {
              lat: rawCoords[idx].lat + Math.sin(angle) * radius,
              lng: rawCoords[idx].lng + Math.cos(angle) * radius,
            };
          });
        });
        displayCoords.forEach((c, i) => {
          const color = dayColor(stopDays?.[i]);
          const stopNumber = i + (originOverride ? 2 : 1);
          const m = new google.maps.Marker({
            position: c,
            map,
            icon: {
              url: `data:image/svg+xml;utf8,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                  <circle cx="16" cy="16" r="14" fill="${color}" stroke="white" stroke-width="2"/>
                  <text x="16" y="21" text-anchor="middle" fill="white" font-size="14" font-weight="700" font-family="system-ui,sans-serif">${stopNumber}</text>
                </svg>`
              )}`,
              scaledSize: new google.maps.Size(32, 32),
              anchor: new google.maps.Point(16, 16),
            },
            label: { text: String(stopNumber), color: "transparent", fontWeight: "600" },
            zIndex: 1000 + i,
          });
          markersRef.current.push(m);
        });
        if (originOverride) {
          const originMarker = new google.maps.Marker({
            position: originOverride,
            map,
            title: originOverride.name
              ? `${language.toLowerCase().startsWith("pt") ? "Ponto de partida" : "Route start"}: ${originOverride.name}`
              : language.toLowerCase().startsWith("pt") ? "Ponto de partida" : "Route start",
            icon: {
              url: `data:image/svg+xml;utf8,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38">
                  <circle cx="19" cy="19" r="17" fill="#00a67a" stroke="white" stroke-width="3"/>
                  <text x="19" y="25" text-anchor="middle" fill="white" font-size="17" font-weight="800" font-family="system-ui,sans-serif">1</text>
                </svg>`
              )}`,
              scaledSize: new google.maps.Size(38, 38),
              anchor: new google.maps.Point(19, 19),
            },
            zIndex: 2000,
          });
          markersRef.current.push(originMarker);
        }
        const b = new google.maps.LatLngBounds();
        displayCoords.forEach((c) => b.extend(c));
        if (originOverride) b.extend(originOverride);
        if (displayCoords.length || originOverride) map.fitBounds(b, 60);
      };

      // If we have a real user origin (e.g. user is in the city), start the
      // route from there instead of the first stop. Falls back to stop 0 when
      // GPS is off or the user is far from the itinerary city.
      const useUserOrigin = originOverride != null && resolved.length >= 1;

      if (!useUserOrigin && resolved.length < 2) {
        renderRoute(resolved);
        return;
      }

      renderRoute(resolved);
      dayRenderersRef.current.forEach(clearDirections);
      const segments = splitRouteIndicesByDay(resolved.map((_, i) => ({ day: stopDays?.[i] })));
      const routable = segments.filter((segment) => segment.indices.length > 1);
      if (!routable.length) return;
      while (dayRenderersRef.current.length < routable.length) {
        dayRenderersRef.current.push(new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          preserveViewport: true,
          polylineOptions: { strokeColor: "#0ea5b7", strokeWeight: 5, strokeOpacity: 0.9 },
        }));
      }
      const collected = new Map<number, RouteLeg[]>();
      let completed = 0;
      let failed = false;
      routable.forEach((segment, segmentIndex) => {
        const indices = segment.indices;
        const dayStops = indices.map((index) => resolved[index]);
        const ds = new google.maps.DirectionsService();
        routeWithFallback(ds, {
          origin: { lat: dayStops[0].lat, lng: dayStops[0].lng },
          destination: { lat: dayStops[dayStops.length - 1].lat, lng: dayStops[dayStops.length - 1].lng },
          waypoints: dayStops.slice(1, -1).map((point) => ({ location: { lat: point.lat, lng: point.lng }, stopover: true })),
          optimizeWaypoints: optimizeOrder && routable.length === 1,
        }, mode, (result, status) => {
          if (cancelled || routeRevisionRef.current !== effectRevision) return;
          completed += 1;
          if (status !== "OK" || !result) {
            failed = true;
          } else {
            dayRenderersRef.current[segmentIndex]?.setDirections(result);
            const legs = result.routes[0]?.legs ?? [];
            collected.set(segmentIndex, legs.map((leg, i) => {
              const rawMinutes = Math.round(((leg.duration_in_traffic?.value ?? leg.duration?.value) ?? 0) / 60);
              return {
                from: dayStops[i]?.name ?? "",
                to: dayStops[i + 1]?.name ?? "",
                minutes: mode === "scooter" ? Math.max(1, Math.round(rawMinutes * 0.7)) : rawMinutes,
                distance: leg.distance?.text,
                steps: (leg.steps ?? []).map((step) => stripHtml(step.instructions)).filter(Boolean),
                fromIndex: indices[i],
                toIndex: indices[i + 1],
              };
            }));
          }
          if (completed !== routable.length) return;
          // Atomic callback: never replace valid legs with a partial/failed set.
          if (failed) return;
          const all = routable.flatMap((_, i) => collected.get(i) ?? []);
          onDurations?.(all.map((leg) => leg.minutes));
          onRouteLegs?.(all);
          onRoutePolyline?.(null);
        }, departureTime);
      });
    });


    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    routeRevision,
    stops.map((s) => `${s.name}:${s.placeId ?? ""}:${s.lat?.toFixed(5) ?? ""}:${s.lng?.toFixed(5) ?? ""}:${s.photoUrl ? "photo" : ""}`).join("|"),
    stopDays?.join("|"),
    mode,
    center.lat,
    center.lng,
    optimizeOrder,
    departureTime?.getTime(),
    mapReadyRevision,
    // Round so we don't re-route on every GPS tick.
    originOverride ? `${originOverride.name ?? ""}:${originOverride.lat.toFixed(3)}:${originOverride.lng.toFixed(3)}` : "none",
  ]);

  const recenter = () => {
    const map = mapRef.current;
    if (!map || !userPosition) return;
    const dest = orderedResolvedRef.current[activeStopIndex];
    if (navigationActive) {
      // First-person-style view: close zoom, tilted, oriented towards destination/movement.
      const heading =
        dest != null
          ? bearingDegrees(userPosition, { lat: dest.lat, lng: dest.lng })
          : smoothedHeadingRef.current ?? 0;
      runProgrammatic(() => {
        map.panTo(userPosition);
        map.setZoom(18);
        map.setTilt(60);
        map.setHeading(heading);
      });
      fittedNavigationKeyRef.current = dest
        ? `${activeStopIndex}:${dest.lat.toFixed(4)}:${dest.lng.toFixed(4)}`
        : fittedNavigationKeyRef.current;
    } else {
      runProgrammatic(() => {
        map.panTo(userPosition);
        map.setZoom(16);
        map.setTilt(0);
        map.setHeading(0);
      });
    }
    setFollowMode(true);
  };


  return (
    <div className="absolute inset-0">
      <div ref={ref} className="h-full w-full" />
      {isDemoModeEnabled() && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/60 p-6 text-center text-sm text-muted-foreground">
          Mapa desativado no modo de teste (sem chamadas pagas ao Google Maps).
        </div>
      )}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-sm">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      )}
      {error && (
        <div className="absolute inset-x-4 top-4 rounded-lg bg-destructive/90 px-3 py-2 text-xs text-destructive-foreground">
          {error}
        </div>
      )}
      {userPosition && (
        <button
          type="button"
          onClick={recenter}
          aria-label="Recentrar na minha localização"
          title="Recentrar"
          className={`absolute z-20 inline-flex items-center gap-2 rounded-full shadow-lg ring-1 transition ${
            navigationActive ? "bottom-24 right-3" : "right-3 top-16"
          } ${
            !followMode
              ? "h-10 px-4 bg-brand text-white ring-brand hover:brightness-110 animate-in fade-in slide-in-from-top-2"
              : "h-10 w-10 justify-center bg-background ring-border hover:bg-muted"
          }`}
        >
          <Navigation className={`h-5 w-5 ${followMode ? "text-brand" : "text-white"}`} />
          {!followMode && <span className="text-sm font-medium">Recentrar</span>}
        </button>
      )}
      {venueMarkers.length > 0 && (
        <div className="absolute bottom-14 left-3 z-20 rounded-md border border-border bg-background/95 px-1.5 py-0.5 shadow-[var(--shadow-soft)] backdrop-blur">
          <div className="text-[7px] font-semibold uppercase tracking-wide text-muted-foreground leading-tight">
            Paragens extra
          </div>
          <div className="flex flex-col text-[8px] font-medium text-foreground leading-tight">
            {venueMarkers.some((v) => v.kind !== "hotel") && <span>🍴 Refeição</span>}
            {venueMarkers.some((v) => v.kind === "hotel") && <span>🛏️ Hotel</span>}
          </div>
        </div>
      )}

      {/* Multi-day legend */}
      {stopDays && stopDays.length > 0 && new Set(stopDays).size > 1 && (
        <div className="absolute bottom-4 left-4 z-20 rounded-xl border border-border bg-background/95 px-3 py-2 shadow-[var(--shadow-soft)] backdrop-blur">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Dias
          </div>
          <div className="flex flex-wrap gap-2">
            {Array.from(new Set(stopDays)).sort((a, b) => a - b).map((d) => (
              <div key={d} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-3 rounded-full ring-1 ring-white"
                  style={{ backgroundColor: dayColor(d) }}
                />
                <span className="text-xs font-medium text-foreground">{d}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
